/**
 * AIChatService.js
 * Service for handling AI chat functionality
 * Handles API calls, message management, and chat history
 */

import axios from 'axios';

class AIChatService {
  constructor() {
    this.baseURL = process.env.REACT_APP_API_URL || 'http://localhost:8090';
    this.apiVersion = '/api';
    this.endpoints = {
      ask: '/ai/ask',
      ask2: '/ai/ask2',
      test: '/ai/test',
      test2: '/ai/test2',
      userQuestions: '/ai/user-questions',
      clientInsights: '/ai/client-insights',
      addHistory: '/ai/add-history',
      chatHistory: '/chat-history/history',
      chatById: '/chat-history/chat',
      saveMessage: '/chat-history/message',
      updateTitle: '/chat-history/chat',
      deleteChat: '/chat-history/chat',
      chatStats: '/chat-history/stats',
      searchChats: '/chat-history/search'
    };
  }

  /**
   * Get authorization headers for API requests
   * @returns {Object} Headers object with authorization token
   */
  getAuthHeaders() {
    const user = localStorage.getItem('user');

    if (user) {
      try {
        const parsedUser = JSON.parse(user);
        return {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${parsedUser.token}`
        };
      } catch (error) {
        console.error('Error parsing user data:', error);
        return {
          'Content-Type': 'application/json'
        };
      }
    }
    return {
      'Content-Type': 'application/json'
    };
  }

  /**
   * Send a message to the AI and get response
   * @param {string} message - User message
   * @returns {Promise<Object>} AI response
   */
  async sendMessage(message) {
    try {
      const payload = {
        question: message.trim()
      };

      const response = await axios.post(
        `${this.baseURL}${this.endpoints.ask}`,
        payload,
        {
          headers: this.getAuthHeaders(),
          timeout: 30000
        }
      );

      return {
        success: true,
        data: response.data,
        error: null
      };
    } catch (error) {
      console.error('Error sending message to AI:', error);
      return {
        success: false,
        data: null,
        error: this.handleError(error)
      };
    }
  }

  /**
   * Send a message using the second AI endpoint with form data
   * @param {string} message - User message
   * @returns {Promise<Object>} AI response
   */
  async sendMessageV2(message) {
    try {
      // Get user token for authorization
      const user = localStorage.getItem('user');
      let authToken = '';

      if (user) {
        try {
          const parsedUser = JSON.parse(user);
          authToken = parsedUser.token;
        } catch (error) {
          console.error('Error parsing user data:', error);
        }
      }

      // Get timezone from global config
      let timezone = 'UTC';
      try {
        if (typeof global !== 'undefined' && global.config && typeof global.config.getTimeZone === 'function') {
          timezone = global.config.getTimeZone() || 'UTC';
        }
      } catch (error) {
        console.error('Error getting timezone:', error);
      }

      // Create form data
      const formData = new URLSearchParams();
      formData.append('query', message.trim());
      formData.append('timezone', timezone);

      console.log('[AIChatService] Sending message via ask2 endpoint...', { messageLength: message.trim().length, timezone });

      const response = await axios.post(
        `/api/ai/ask2`,
        formData,
        {
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
            //'Authorization': authToken,
            'Authorization': `Bearer ${authToken}`
          },
          withCredentials: true,
          timeout: 300000 // 5 minutes to match backend timeout for AI processing
        }
      );

      console.log('[AIChatService] Received response from ask2 endpoint');

      return {
        success: true,
        data: response.data,
        error: null
      };
    } catch (error) {
      console.error('Error sending message to AI v2:', error);
      return {
        success: false,
        data: null,
        error: this.handleError(error)
      };
    }
  }


  // User Chat
  async sendUserMessageV2(message) {
    try {
      // Get user token for authorization
      const user = localStorage.getItem('user');
      let authToken = '';

      if (user) {
        try {
          const parsedUser = JSON.parse(user);
          authToken = parsedUser.token;
        } catch (error) {
          console.error('Error parsing user data:', error);
        }
      }

      // Get timezone from global config
      let timezone = 'UTC';
      try {
        if (typeof global !== 'undefined' && global.config && typeof global.config.getTimeZone === 'function') {
          timezone = global.config.getTimeZone() || 'UTC';
        }
      } catch (error) {
        console.error('Error getting timezone:', error);
      }

      // Create form data
      const formData = new URLSearchParams();
      formData.append('query', message.trim());
      formData.append('timezone', timezone);

      console.log('[AIChatService] Sending message via ask2 endpoint...', { messageLength: message.trim().length, timezone });

      const response = await axios.post(
        `/api/ai/ask2-user`,
        formData,
        {
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
            //'Authorization': authToken,
            'Authorization': `Bearer ${authToken}`
          },
          withCredentials: true,
          timeout: 300000 // 5 minutes to match backend timeout for AI processing
        }
      );

      console.log('[AIChatService] Received response from ask2 endpoint');

      return {
        success: true,
        data: response.data,
        error: null
      };
    } catch (error) {
      console.error('Error sending message to AI v2:', error);
      return {
        success: false,
        data: null,
        error: this.handleError(error)
      };
    }
  }

  /**
   * Test AI without authentication
   * @param {string} message - User message
   * @returns {Promise<Object>} AI response
   */
  async testAI(message) {
    try {
      const payload = {
        question: message.trim()
      };

      const response = await axios.post(
        `${this.baseURL}${this.endpoints.test}`,
        payload,
        {
          headers: {
            'Content-Type': 'application/json'
          },
          timeout: 30000
        }
      );

      return {
        success: true,
        data: response.data,
        error: null
      };
    } catch (error) {
      console.error('Error testing AI:', error);
      return {
        success: false,
        data: null,
        error: this.handleError(error)
      };
    }
  }

  /**
   * Save chat history
   * @param {string} messages - User messages
   * @param {string} response - AI response
   * @returns {Promise<Object>} Save result
   */
  async saveChatHistory(messages, response) {
    try {
      const user = localStorage.getItem('user');
      let userId = null;

      if (user) {
        try {
          const parsedUser = JSON.parse(user);
          userId = parsedUser._id;
        } catch (error) {
          console.error('Error parsing user data:', error);
        }
      }

      const payload = {
        user: userId,
        messages: messages,
        response: response
      };

      const response_data = await axios.post(
        `${this.baseURL}${this.endpoints.addHistory}`,
        payload,
        {
          headers: this.getAuthHeaders(),
          timeout: 10000
        }
      );

      return {
        success: true,
        data: response_data.data,
        error: null
      };
    } catch (error) {
      console.error('Error saving chat history:', error);
      return {
        success: false,
        data: null,
        error: this.handleError(error)
      };
    }
  }

  /**
   * Get user questions
   * @returns {Promise<Object>} User questions data
   */
  async getUserQuestions() {
    try {
      const response = await axios.get(
        `${this.baseURL}${this.endpoints.userQuestions}`,
        {
          headers: this.getAuthHeaders(),
          timeout: 10000
        }
      );

      return {
        success: true,
        data: response.data,
        error: null
      };
    } catch (error) {
      console.error('Error fetching user questions:', error);
      return {
        success: false,
        data: [],
        error: this.handleError(error)
      };
    }
  }

  /**
   * Clear user questions
   * @returns {Promise<Object>} Clear result
   */
  async clearUserQuestions() {
    try {
      const response = await axios.delete(
        `${this.baseURL}${this.endpoints.userQuestions}`,
        {
          headers: this.getAuthHeaders(),
          timeout: 10000
        }
      );

      return {
        success: true,
        data: response.data,
        error: null
      };
    } catch (error) {
      console.error('Error clearing user questions:', error);
      return {
        success: false,
        data: null,
        error: this.handleError(error)
      };
    }
  }

  /**
   * Get client insights
   * @param {string} question - Question for insights
   * @returns {Promise<Object>} Client insights
   */
  async getClientInsights(question) {
    try {
      const payload = {
        question: question.trim()
      };

      const response = await axios.post(
        `${this.baseURL}${this.endpoints.clientInsights}`,
        payload,
        {
          headers: this.getAuthHeaders(),
          timeout: 30000
        }
      );

      return {
        success: true,
        data: response.data,
        error: null
      };
    } catch (error) {
      console.error('Error getting client insights:', error);
      return {
        success: false,
        data: null,
        error: this.handleError(error)
      };
    }
  }

  /**
   * Generate a fallback response when AI is unavailable
   * @param {string} userMessage - User's message
   * @returns {string} Fallback response
   */
  generateFallbackResponse(userMessage) {
    const fallbackResponses = [
      "I understand you're asking about that. Let me help you with some information based on your question.",
      "That's a great question! Here's what I can tell you about that topic.",
      "I'd be happy to assist you with that. Let me provide you with some helpful information.",
      "Based on your question, here are some steps you can follow to resolve this issue.",
      "I can help you with that! Let me give you some guidance on how to proceed.",
      "That's an interesting question. Here's what I know about that subject.",
      "I'm here to help! Let me provide you with some useful information about your query.",
      "Great question! Here's what I can tell you to help you out."
    ];

    // Simple keyword matching for more relevant responses
    const message = userMessage.toLowerCase();

    if (message.includes('timezone') || message.includes('time zone')) {
      return "To change your timezone in Talenton, follow these steps:\n1. Click the profile icon in the top navigation bar\n2. Navigate to 'Preferences'\n3. Find and click your timezone\n4. Your update will be automatically saved\n\nThis ensures your local time zone is used for events, working hours, and other time-based features.";
    }

    if (message.includes('notification') || message.includes('notify')) {
      return "You can manage notifications from your Account Settings → Notifications. Toggle email or in-app alerts and set quiet hours if needed.";
    }

    if (message.includes('project') || message.includes('create project')) {
      return "To create a new project, go to the Projects section and click the 'Add Project' button. You can then add team members, set deadlines, and track progress.";
    }

    if (message.includes('export') || message.includes('download')) {
      return "Yes, you can export your data from the Reports section in various formats including PDF, Excel, and CSV.";
    }

    if (message.includes('team') || message.includes('member')) {
      return "You can add team members through the Users section in your dashboard. Invite them via email and assign appropriate roles.";
    }

    if (message.includes('time') || message.includes('track')) {
      return "Use the Time Tracker feature to log your working hours and activities. You can start/stop timers and add detailed descriptions.";
    }

    if (message.includes('integrate') || message.includes('integration')) {
      return "Talenton supports various integrations including Google Calendar, Microsoft Calendar, and other productivity tools. Check the Integrations section for available options.";
    }

    return fallbackResponses[Math.floor(Math.random() * fallbackResponses.length)];
  }

  /**
   * Handle API errors and return user-friendly messages
   * @param {Error} error - Error object
   * @returns {string} User-friendly error message
   */
  handleError(error) {
    if (error.response) {
      // Server responded with error status
      const status = error.response.status;
      const message = error.response.data?.message || error.response.data?.error || 'An error occurred';

      switch (status) {
        case 401:
          return 'Please log in to continue using the AI assistant.';
        case 403:
          return 'You do not have permission to access this feature.';
        case 404:
          return 'The requested resource was not found.';
        case 429:
          return 'Too many requests. Please wait a moment before trying again.';
        case 500:
          return 'Server error. Please try again later.';
        default:
          return `Error: ${message}`;
      }
    } else if (error.request) {
      // Network error
      return 'Unable to connect to the server. Please check your internet connection.';
    } else {
      // Other error
      return 'An unexpected error occurred. Please try again.';
    }
  }

  /**
   * Check if the service is available
   * @returns {boolean} Service availability status
   */
  isServiceAvailable() {
    return !!this.baseURL;
  }

  /**
   * Save a message to today's chat
   * @param {Object} message - Message object with id, type, content, timestamp, suggestions
   * @param {string} firstQuestion - First question of the day (for title)
   * @returns {Promise<Object>} Save result
   */
  async saveMessageToHistory(message, firstQuestion = null) {
    try {
      const response = await axios.post(
        `${this.baseURL}/api${this.endpoints.saveMessage}`,
        {
          message,
          firstQuestion
        },
        {
          headers: this.getAuthHeaders(),
          timeout: 10000
        }
      );

      return {
        success: true,
        data: response.data,
        error: null
      };
    } catch (error) {
      console.error('Error saving message to history:', error);
      return {
        success: false,
        data: null,
        error: this.handleError(error)
      };
    }
  }

  /**
   * Get user's chat history
   * @param {number} page - Page number
   * @param {number} limit - Number of chats per page
   * @returns {Promise<Object>} Chat history
   */
  async getChatHistory(page = 1, limit = 20) {
    try {
      const response = await axios.get(
        `${this.baseURL}/api${this.endpoints.chatHistory}`,
        {
          params: { page, limit },
          headers: this.getAuthHeaders(),
          timeout: 10000
        }
      );

      return {
        success: true,
        data: response.data,
        error: null
      };
    } catch (error) {
      console.error('Error fetching chat history:', error);
      return {
        success: false,
        data: { chats: [], pagination: { page: 1, limit: 20, total: 0 } },
        error: this.handleError(error)
      };
    }
  }

  /**
   * Get specific chat by ID
   * @param {string} chatId - Chat ID
   * @returns {Promise<Object>} Chat data
   */
  async getChatById(chatId) {
    try {
      const response = await axios.get(
        `${this.baseURL}/api${this.endpoints.chatById}/${chatId}`,
        {
          headers: this.getAuthHeaders(),
          timeout: 10000
        }
      );

      return {
        success: true,
        data: response.data,
        error: null
      };
    } catch (error) {
      console.error('Error fetching chat by ID:', error);
      return {
        success: false,
        data: null,
        error: this.handleError(error)
      };
    }
  }

  /**
   * Update chat title
   * @param {string} chatId - Chat ID
   * @param {string} title - New title
   * @returns {Promise<Object>} Update result
   */
  async updateChatTitle(chatId, title) {
    try {
      const response = await axios.put(
        `${this.baseURL}/api${this.endpoints.updateTitle}/${chatId}/title`,
        { title },
        {
          headers: this.getAuthHeaders(),
          timeout: 10000
        }
      );

      return {
        success: true,
        data: response.data,
        error: null
      };
    } catch (error) {
      console.error('Error updating chat title:', error);
      return {
        success: false,
        data: null,
        error: this.handleError(error)
      };
    }
  }

  /**
   * Delete a chat
   * @param {string} chatId - Chat ID
   * @returns {Promise<Object>} Delete result
   */
  async deleteChat(chatId) {
    try {
      const response = await axios.delete(
        `${this.baseURL}/api${this.endpoints.deleteChat}/${chatId}`,
        {
          headers: this.getAuthHeaders(),
          timeout: 10000
        }
      );

      return {
        success: true,
        data: response.data,
        error: null
      };
    } catch (error) {
      console.error('Error deleting chat:', error);
      return {
        success: false,
        data: null,
        error: this.handleError(error)
      };
    }
  }

  /**
   * Search chats
   * @param {string} query - Search query
   * @param {number} page - Page number
   * @param {number} limit - Number of results per page
   * @returns {Promise<Object>} Search results
   */
  async searchChats(query, page = 1, limit = 20) {
    try {
      const response = await axios.get(
        `${this.baseURL}/api${this.endpoints.searchChats}`,
        {
          params: { q: query, page, limit },
          headers: this.getAuthHeaders(),
          timeout: 10000
        }
      );

      return {
        success: true,
        data: response.data,
        error: null
      };
    } catch (error) {
      console.error('Error searching chats:', error);
      return {
        success: false,
        data: { chats: [], pagination: { page: 1, limit: 20, total: 0 } },
        error: this.handleError(error)
      };
    }
  }

  /**
   * Get chat statistics
   * @returns {Promise<Object>} Chat statistics
   */
  async getChatStats() {
    try {
      const response = await axios.get(
        `${this.baseURL}/api${this.endpoints.chatStats}`,
        {
          headers: this.getAuthHeaders(),
          timeout: 10000
        }
      );

      return {
        success: true,
        data: response.data,
        error: null
      };
    } catch (error) {
      console.error('Error fetching chat stats:', error);
      return {
        success: false,
        data: null,
        error: this.handleError(error)
      };
    }
  }


  /**
   * Get service status and configuration
   * @returns {Object} Service status information
   */
  getServiceStatus() {
    return {
      available: this.isServiceAvailable(),
      baseURL: this.baseURL,
      apiVersion: this.apiVersion,
      endpoints: this.endpoints
    };
  }
}

// Create and export a singleton instance
const aiChatService = new AIChatService();
export default aiChatService;

// Export the class for testing purposes
export { AIChatService };